import React, { useState, useEffect, useRef } from 'react';
import {
   Box,
   Typography,
   Grid,
   Paper,
   IconButton,
   Button,
   CircularProgress,
   Alert,
   Snackbar,
   Card,
   CardMedia,
   CardActions,
   Menu,
   MenuItem,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   Chip,
   CardContent,
} from '@mui/material';
import {
   PhotoCamera,
   MoreVert,
   AccountCircle,
   Delete,
   PhotoLibrary,
   Add as AddIcon,
   AutoAwesome,
   AutoFixHigh,
} from '@mui/icons-material';
import {
   getStorage,
   ref,
   uploadBytes,
   getDownloadURL,
   listAll,
   deleteObject,
} from 'firebase/storage';
import {
   doc,
   updateDoc,
   arrayUnion,
   arrayRemove,
   getDoc,
   setDoc,
   Timestamp,
   deleteDoc,
   addDoc,
   collection,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { updateProfile } from 'firebase/auth';
import { auth } from '../../../firebase/config';
import StyleTransformModal from '../../../components/PhotoTransform/StyleTransformModal';
import EnhancePhotoModal from '../../../components/PhotoTransform/EnhancePhotoModal';
import {
   get_user_photos,
   get_user_profile_photos,
   get_user_profile_transformed_photo,
} from '../../../services';
import { convertToJPEG, getImageTakenDate } from '../../../utils/imageUtils';
import { useUser } from '../../../contexts/UserContext';
import AIEnhanceImageButton from '../../../components/AIEnhanceButton';

interface Photo {
   id: string;
   url: string;
   timestamp: number;
   isProfile?: boolean;
   isTransformed: boolean;
   originalPhotoId?: string;
   isEnhanced?: boolean;
   path?: string;
}

interface ProfilePhotosProps {
   userId: string;
   currentProfilePhoto?: string;
   onProfilePhotoUpdate: (url: string) => void;
   onCoverPhotoUpdate?: (url: string) => void;
}

const ProfilePhotos: React.FC<ProfilePhotosProps> = ({
   userId,
   currentProfilePhoto,
   onProfilePhotoUpdate,
   onCoverPhotoUpdate,
}) => {
   const [photos, setPhotos] = useState<Photo[]>([]);
   const [loading, setLoading] = useState(false);
   const [uploading, setUploading] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [success, setSuccess] = useState<string | null>(null);
   const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
   const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
   const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
   const fileInputRef = useRef<HTMLInputElement>(null);
   const [transformModalOpen, setTransformModalOpen] = useState(false);
   const [isEnhanceLoading, setIsEnhanceLoading] = useState(false);
   const [selectedPhotoForTransform, setSelectedPhotoForTransform] = useState<{
      id: string;
      url: string;
   } | null>(null);
   const [enhanceModalOpen, setEnhanceModalOpen] = useState(false);
   const [selectedPhotoForEnhance, setSelectedPhotoForEnhance] = useState<{
      id: string;
      url: string;
   } | null>(null);
   const { currentUser } = useUser();
   const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
   const [snackbarMessage, setSnackbarMessage] = useState<string>('');
   const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);

   const handleCloseSnackbar = () => {
      setSnackbarOpen(false);
   };

   // Fetch user's photos on component mount
   useEffect(() => {
      if (userId) {
         fetchPhotos();
      }
   }, [userId]);

   useEffect(() => {
      fetchPhotos();
   }, [transformModalOpen]);

   const fetchPhotos = async () => {
      setLoading(true);
      setError(null);
      try {
         console.log('Fetching photos for userId:', userId);

         // Get photos from Firestore
         const transformedPhotos = (
            (await get_user_profile_transformed_photo(userId || '')) || []
         ).map((photo: any) => ({
            ...photo,
            id: photo.id,
            url: photo.url,
            timestamp: photo.createdAt
               ? typeof photo.createdAt === 'string'
                  ? new Date(photo.createdAt)
                  : photo.createdAt.toDate()
               : new Date(),
            isProfile: photo.url === currentProfilePhoto,
            takenAt: photo.takenAt
               ? typeof photo.takenAt === 'string'
                  ? new Date(photo.takenAt)
                  : photo.takenAt.toDate()
               : null,
         }));

         console.log('Transformed photos:', transformedPhotos);
         
         let userPhotos = ((await get_user_profile_photos(userId)) || []).map((photo: any) => ({
            ...photo,
            id: photo.id,
            url: photo.url,
            timestamp: photo.createdAt
               ? typeof photo.createdAt === 'string'
                  ? new Date(photo.createdAt)
                  : photo.createdAt.toDate()
               : new Date(),
            isProfile: photo.url === currentProfilePhoto,
            takenAt: photo.takenAt
               ? typeof photo.takenAt === 'string'
                  ? new Date(photo.takenAt)
                  : photo.takenAt.toDate()
               : null,
         }));

         console.log('User profile photos:', userPhotos);

         userPhotos = [...transformedPhotos, ...userPhotos];

         userPhotos.sort(
            (a: any, b: any) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
         );

         // Mark current profile photo
         userPhotos = userPhotos.map((photo: any) => ({
            ...photo,
            isProfile: photo.url === currentProfilePhoto,
         }));

         console.log('Final processed photos:', userPhotos);
         setPhotos(userPhotos);
      } catch (err) {
         console.error('Error fetching photos:', err);
         setError('Failed to load photos');
      } finally {
         setLoading(false);
      }
   };

   const handleUploadClick = () => {
      if (fileInputRef.current) {
         fileInputRef.current.click();
      }
   };

   const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!userId || !e.target.files || e.target.files.length === 0) {
         return;
      }

      const files = e.target.files;
      let uploadedPhotos = 0;
      let failedPhotos = 0;
      setUploading(true);
      setError(null);

      const allPromises = Promise.all(
         Array.from(files).map(async (file) => {
            try {
               if (file.size > 150 * 1024 * 1024) {
                  return null;
               }
               const metadata = await getImageTakenDate(file);

               // Generate unique filename for storage
               const storageFileName = `profile_${Date.now()}_${Math.random().toString(36).substring(2)}`;
               const storage = getStorage();
               const storageRef = ref(
                  storage,
                  `user_photos/${userId}/${storageFileName}.${file.name.split('.').pop()}`
               );

               await uploadBytes(storageRef, file);

               const photoURL = await getDownloadURL(storageRef);

               const newPhoto = {
                  url: photoURL || null,
                  title: file.name.replace(/\.[^/.]+$/, ''),
                  description: '',
                  fileName: file.name,
                  size: file.size,
                  mimeType: file.type,
                  isTransformed: false,
                  userId: userId,
                  uploadedBy: userId,
                  type: 'user_photo',
                  createdAt: Timestamp.now(),
                  uploadedAt: Timestamp.now(),
                  takenAt: metadata.takenDate
                     ? Timestamp.fromDate(metadata.takenDate)
                     : Timestamp.now(),
                  tags: [],
                  likeCount: 0,
                  likedBy: [],
                  ...(metadata.location?.longitude &&
                     metadata.location?.latitude && {
                        location: {
                           address: '',
                           name: '',
                           coordinates: {
                              latitude: metadata.location?.latitude || 0,
                              longitude: metadata.location?.longitude || 0,
                           },
                        },
                     }),
               };

               const photoRef = await addDoc(collection(db, 'photos'), newPhoto);

               uploadedPhotos++;
               return newPhoto;
            } catch (err) {
               failedPhotos++;
               return null;
            }
         })
      );

      try {
         const uploadedPhotos = await allPromises;
         const photos = uploadedPhotos.filter((photo) => photo != null);
         const failedPhotos = uploadedPhotos.filter((photo) => photo == null);
         setPhotos((prev: any[]) => [...photos, ...prev]);
         setSuccess(
            `${uploadedPhotos.length || 0} photos uploaded successfully, ${failedPhotos.length || 0} photos failed`
         );
         //  }
      } catch (err) {
         setError('Failed to upload photo');
      } finally {
         setUploading(false);
         if (fileInputRef.current) {
            fileInputRef.current.value = '';
         }
      }
   };

   const handlePhotoClick = (photo: Photo) => {
      // Preview photo in larger size
   };

   const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, photo: Photo) => {
      setAnchorEl(event.currentTarget);
      setSelectedPhoto(photo);
   };

   const handleMenuClose = () => {
      setAnchorEl(null);
   };

   const handleSetAsProfile = async () => {
      if (!selectedPhoto) return;

      handleMenuClose();
      setLoading(true);

      try {
         // Call backend API to update profile and sync across all collections
         const token = await auth.currentUser?.getIdToken();
         const response = await fetch(`${process.env.REACT_APP_API_URL}/api/profile/${userId}`, {
            method: 'PUT',
            headers: {
               'Content-Type': 'application/json',
               Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ photoURL: selectedPhoto.url }),
         });

         if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to update profile photo');
         }

         console.log('✅ Profile photo updated and synced across all collections');

         // Update local state to mark this as profile photo
         setPhotos((prev) =>
            prev.map((photo) => ({
               ...photo,
               isProfile: photo.id === selectedPhoto.id,
            }))
         );

         // Notify parent component if needed
         if (onProfilePhotoUpdate) {
            onProfilePhotoUpdate(selectedPhoto.url);
         }

         setSuccess('Profile photo updated successfully');
      } catch (err) {
         console.error('Error setting profile photo:', err);
         setError('Failed to set profile photo');
      } finally {
         setLoading(false);
      }
   };

   const handleSetAsCoverPhoto = async () => {
      if (!selectedPhoto || !userId) return;

      handleMenuClose();
      setLoading(true);

      try {
         // Update user profile in Firestore
         const userProfileRef = doc(db, 'userProfiles', userId);
         await updateDoc(userProfileRef, {
            coverPhotoURL: selectedPhoto.url,
         });

         // Notify parent component
         if (onCoverPhotoUpdate) {
            onCoverPhotoUpdate(selectedPhoto.url);
         }

         setSuccess('Cover photo updated successfully');
      } catch (err) {
         console.error('Error setting cover photo:', err);
         setError('Failed to update cover photo');
      } finally {
         setLoading(false);
      }
   };

   const confirmDeletePhoto = () => {
      setOpenDeleteDialog(true);
   };

   const handleDeleteCancel = () => {
      setOpenDeleteDialog(false);
   };

   const handleDeleteConfirm = async () => {
      if (!selectedPhoto) return;

      setOpenDeleteDialog(false);
      handleMenuClose();
      setLoading(true);

      try {
         // Cannot delete if it's the profile photo
         if (selectedPhoto.isProfile) {
            setError(
               'Cannot delete your current profile photo. Please set a different profile photo first.'
            );
            return;
         }

         await deleteDoc(doc(db, 'photos', selectedPhoto.id));

         // Update local state
         setPhotos((prev) => prev.filter((photo) => photo.id !== selectedPhoto.id));

         setSuccess('Photo deleted successfully');
      } catch (err) {
         console.error('Error deleting photo:', err);
         setError('Failed to delete photo');
      } finally {
         setLoading(false);
      }
   };

   const handleOpenTransformModal = (photo: { id: string; url: string }) => {
      setSelectedPhotoForTransform(photo);
      setTransformModalOpen(true);
   };

   const handleViewOriginal = (originalPhotoId: string) => {
      const originalPhoto = photos.find((photo) => photo.id === originalPhotoId);
      if (originalPhoto) {
         // Open the original photo in a viewer or modal
         window.open(originalPhoto.url, '_blank');
      }
   };

   const handleOpenEnhanceModal = (photo: { id: string; url: string }) => {
      setSelectedPhotoForEnhance(photo);
      setEnhanceModalOpen(true);
   };

   const handleEnhanceComplete = async (enhancedUrl: string, basePhotoId: string) => {
      try {
         setIsEnhanceLoading(true);
         setSelectedPhotoForTransform({
            id: basePhotoId,
            url: enhancedUrl,
         });

         await handleSave({
            transformedUrl: enhancedUrl,
            basePhotoId: basePhotoId,
         });

         setPhotos((prev) => [
            ...prev,
            {
               id: `enhanced_${Date.now()}`,
               url: enhancedUrl,
               timestamp: Date.now(),
               isTransformed: true,
               takenAt: Timestamp.now().toDate(),
            },
         ]);
         setSnackbarSeverity('success');
         setSnackbarMessage('Photo enhanced successfully');
         setSnackbarOpen(true);
      } catch (error) {
         setSnackbarSeverity('error');
         setSnackbarMessage('Failed to enhance photo');
         setSnackbarOpen(true);
      } finally {
         setIsEnhanceLoading(false);
      }
   };

   const handleSave = async (photo: any) => {
      if (!currentUser?.uid) {
         return;
      }

      try {
         if (!photo) {
            setSnackbarSeverity('error');
            setSnackbarMessage('Photo not found');
            setSnackbarOpen(true);
            return;
         }
         const { blob, extension } = await convertToJPEG(photo?.transformedUrl);

         // Generate unique filename for storage
         const storageFileName = `enhanced_${Date.now()}_${Math.random().toString(36).substring(2)}`;
         const path = `user_photos/${currentUser?.uid}/${storageFileName}.${extension}`;

         // Upload to Firebase Storage
         const storage = getStorage();
         const storageRef = ref(storage, path);

         // Upload file
         const uploadResult = await uploadBytes(storageRef, blob);

         const photoUrl = await getDownloadURL(storageRef);

         const newPhoto = {
            url: photoUrl,
            title: `Enhanced - Photo`,
            description: `AI enhanced version of photo`,
            fileName: `${storageFileName}.${extension}`,
            size: blob.size,
            mimeType: blob.type,
            isTransformed: true,
            userId: currentUser?.uid,
            uploadedBy: currentUser?.uid,
            type: 'user_photo',
            createdAt: Timestamp.now(),
            uploadedAt: Timestamp.now(),
            takenAt: Timestamp.now(),
            basePhotoId: selectedPhotoForTransform?.id || null,
            tags: [],
            likeCount: 0,
            likedBy: [],
         };

         const photoRef = await addDoc(collection(db, 'photos'), newPhoto);

         setSnackbarSeverity('success');
         setSnackbarMessage('Photo saved successfully');
         setSnackbarOpen(true);
      } catch (err) {
         setSnackbarSeverity('error');
         setSnackbarMessage('Failed to upload photo');
         setSnackbarOpen(true);
      } finally {
      }
   };

   const formatDate = (date: string) => {
      try {
         return new Date(date).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
         });
      } catch (err) {
         return '';
      }
   };

   return (
      <Box>
         {/* Intro section with explanation */}
         <Box sx={{ mb: 4 }}>
            <Typography variant='h5' gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
               <PhotoLibrary sx={{ mr: 1, color: 'primary.main' }} />
               Photo Gallery
            </Typography>
            <Typography variant='body1' color='text.secondary' sx={{ mb: 2 }}>
               Share your travel memories and experiences. Upload photos from your trips to create a
               visual journal of your adventures.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
               <Chip
                  icon={<AutoAwesome fontSize='small' />}
                  label='AI Enhancement Available'
                  variant='outlined'
                  color='secondary'
               />
               <Chip
                  icon={<AutoFixHigh fontSize='small' />}
                  label='Style Transformation'
                  variant='outlined'
                  color='primary'
               />
               <Chip
                  icon={<AccountCircle fontSize='small' />}
                  label='Set as Profile Photo'
                  variant='outlined'
                  color='info'
               />
            </Box>
         </Box>

         {/* Header with upload button */}
         <Box
            sx={{
               display: 'flex',
               justifyContent: 'space-between',
               alignItems: 'center',
               mb: 3,
               backgroundColor: '#00C4A7',
               p: 2,
               borderRadius: 2,
            }}
         >
            <Typography variant='h6' sx={{ color: '#000', fontWeight: 700 }}>
               Your Travel Memories
            </Typography>
            <Button
               variant='contained'
               startIcon={<AddIcon />}
               onClick={handleUploadClick}
               disabled={uploading}
               sx={{
                  bgcolor: '#000',
                  color: '#00C4A7',
                  fontWeight: 600,
                  boxShadow: 2,
                  '&:hover': {
                     bgcolor: '#1a1a1a',
                     transform: 'translateY(-2px)',
                     transition: 'all 0.2s',
                  },
               }}
            >
               {uploading ? 'Uploading...' : 'Add Photo'}
            </Button>
         </Box>

         {/* Hidden file input */}
         <input
            multiple={true}
            type='file'
            accept='image/*'
            style={{ display: 'none' }}
            ref={fileInputRef}
            onChange={handleFileUpload}
         />

         {/* Error and success alerts */}
         {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
               {error}
            </Alert>
         )}

         <Snackbar
            open={!!success}
            autoHideDuration={4000}
            onClose={() => setSuccess(null)}
            message={success}
         />

         {/* Photos grid */}
         {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
               <CircularProgress />
            </Box>
         ) : photos.length === 0 ? (
            <Paper
               sx={{
                  p: 5,
                  textAlign: 'center',
                  backgroundColor: 'rgba(255,255,255,0.03)',
                  borderRadius: 2,
                  border: '1px solid rgba(255,255,255,0.08)',
                  boxShadow: 'none',
               }}
            >
               <PhotoCamera sx={{ fontSize: 80, color: '#00C4A7', opacity: 0.8, mb: 2 }} />
               <Typography variant='h5' gutterBottom fontWeight='medium'>
                  No Photos Yet
               </Typography>
               <Typography color='text.secondary' sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}>
                  Share your travel experiences! Upload photos from your trips to create a
                  personalized gallery that enhances your profile and shows off your adventures.
               </Typography>
               <Button
                  variant='contained'
                  size='large'
                  startIcon={<AddIcon />}
                  onClick={handleUploadClick}
                  sx={{
                     py: 1.5,
                     px: 4,
                     boxShadow: 3,
                     '&:hover': {
                        transform: 'translateY(-2px)',
                        transition: 'all 0.2s',
                     },
                  }}
               >
                  Upload Your First Photo
               </Button>
               <Typography variant='caption' display='block' sx={{ mt: 4 }}>
                  Supported formats: JPEG, PNG, GIF • Max file size: 150MB
               </Typography>
            </Paper>
         ) : (
            <>
               <Grid container spacing={3}>
                  {photos.map((photo: any) => (
                     <Grid item xs={12} sm={6} md={4} lg={3} xl={4} key={photo.id}>
                        <Card
                           sx={{
                              p: 0,
                              position: 'relative',
                              borderRadius: 2,
                              overflow: 'hidden',
                              boxShadow: 2,
                              transition: 'all 0.3s',
                              '&:hover': {
                                 boxShadow: 6,
                                 transform: 'translateY(-4px)',
                              },
                              ...(photo.isProfile && {
                                 border: '2px solid',
                                 borderColor: 'primary.main',
                              }),
                           }}
                        >
                           <CardMedia
                              component='img'
                              height='200'
                              image={photo.url}
                              alt='User photo'
                              onClick={() => handlePhotoClick(photo)}
                              sx={{
                                 cursor: 'pointer',
                                 objectFit: 'cover',
                              }}
                           />
                           <CardContent sx={{ p: 0.5 }}>
                              <Typography
                                 variant='body2'
                                 color='text.secondary'
                                 sx={{ fontSize: '0.7rem' }}
                              >
                                 {photo?.takenAt && `Taken At : ${formatDate(photo.takenAt)}`}
                              </Typography>
                           </CardContent>
                           <CardActions
                              sx={{
                                 display: 'flex',
                                 justifyContent: !photo.isTransformed
                                    ? 'flex-end'
                                    : 'space-between',
                              }}
                           >
                              {/* {photo.isProfile ? (
                                 <Chip
                                    size='small'
                                    icon={<AccountCircle fontSize='small' />}
                                    label='Profile Photo'
                                    color='primary'
                                    variant='filled'
                                 />
                              ) : (
                                 <Button
                                    size='small'
                                    startIcon={<AccountCircle />}
                                    onClick={() => {
                                       setSelectedPhoto(photo);
                                       handleSetAsProfile();
                                    }}
                                    sx={{ fontSize: '0.75rem' }}
                                 >
                                    Set as Profile
                                 </Button>
                              )} */}
                              {photo.isTransformed && (
                                 <Chip
                                    label='Style Transformed'
                                    size='small'
                                    color='primary'
                                    icon={<AutoFixHigh fontSize='small' />}
                                    sx={{
                                       // position: 'absolute',
                                       // bottom: 50,
                                       // left: 8,
                                       backgroundColor: 'rgba(25, 118, 210, 0.85)',
                                       backdropFilter: 'blur(4px)',
                                       color: 'white',
                                       '& .MuiChip-icon': { color: 'white' },
                                       fontSize: '0.7rem',
                                    }}
                                 />
                              )}
                              <IconButton
                                 size='small'
                                 onClick={(event) => {
                                    setAnchorEl(event.currentTarget);
                                    setSelectedPhoto(photo);
                                 }}
                              >
                                 <MoreVert />
                              </IconButton>
                           </CardActions>
                           <Box
                              sx={{
                                 position: 'absolute',
                                 top: 10,
                                 right: 10,
                                 display: 'flex',
                                 gap: 1,
                                 alignItems: 'center',
                                 justifyContent: 'center',
                              }}
                           >
                              <AIEnhanceImageButton
                                 photoUrl={photo.url}
                                 Icon={
                                    <IconButton
                                       sx={{
                                          width: '20px',
                                          height: '20px',
                                          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                          color: 'white',
                                          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
                                       }}
                                       title='Enhance photo with AI'
                                    >
                                       <AutoAwesome />
                                    </IconButton>
                                 }
                                 onDone={(images: any[], photoId: string) => {
                                    handleEnhanceComplete(images[0].url || '', photoId);
                                 }}
                                 photoId={photo.id}
                              />

                              <IconButton
                                 sx={{
                                    width: '20px',
                                    height: '20px',
                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                    color: 'white',
                                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
                                 }}
                                 onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenTransformModal({ id: photo.id, url: photo.url });
                                 }}
                                 title='Apply artistic styles'
                              >
                                 <AutoFixHigh />
                              </IconButton>
                           </Box>

                           {/* Add back the enhanced and transformed labels with improved styling */}
                        </Card>
                     </Grid>
                  ))}
               </Grid>
            </>
         )}

         <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
            {selectedPhoto && !selectedPhoto.isProfile && (
               <MenuItem onClick={handleSetAsProfile}>
                  <AccountCircle fontSize='small' sx={{ mr: 1 }} />
                  Set as Profile Photo
               </MenuItem>
            )}
            <MenuItem onClick={handleSetAsCoverPhoto}>
               <PhotoLibrary fontSize='small' sx={{ mr: 1 }} />
               Set as Cover Photo
            </MenuItem>
            <MenuItem
               onClick={() =>
                  selectedPhoto &&
                  handleOpenTransformModal({
                     id: selectedPhoto.id,
                     url: selectedPhoto.url,
                  })
               }
            >
               <AutoAwesome fontSize='small' sx={{ mr: 1 }} />
               Transform with AI
            </MenuItem>
            <MenuItem onClick={confirmDeletePhoto}>
               <Delete fontSize='small' sx={{ mr: 1 }} />
               Delete Photo
            </MenuItem>
         </Menu>

         {/* Delete confirmation dialog */}
         <Dialog open={openDeleteDialog} onClose={handleDeleteCancel}>
            <DialogTitle>Delete Photo?</DialogTitle>
            <DialogContent>
               Are you sure you want to delete this photo? This action cannot be undone.
            </DialogContent>
            <DialogActions>
               <Button onClick={handleDeleteCancel}>Cancel</Button>
               <Button onClick={handleDeleteConfirm} color='error'>
                  Delete
               </Button>
            </DialogActions>
         </Dialog>

         {selectedPhotoForTransform && (
            <StyleTransformModal
               open={transformModalOpen}
               onClose={() => setTransformModalOpen(false)}
               photoUrl={selectedPhotoForTransform.url}
               photoId={selectedPhotoForTransform.id}
               onSave={handleSave}
               // onTransformComplete={handleTransformComplete}
            />
         )}

         <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
         >
            <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
               {snackbarMessage}
            </Alert>
         </Snackbar>
      </Box>
   );
};

export default ProfilePhotos;
